import { ENV_NAME, getEnvironment } from '@heidi/api/config';

import * as amplitude from '@amplitude/analytics-browser';

import { pushAnalyticsInspectorEvent } from './inspector/store';
import { forwardWidgetAnalyticsEvent } from './inspector/widget-bridge';

export type AnalyticsDebuggerEvent = {
  destination: string;
  eventName: string;
  props: Record<string, unknown>;
};

type AnalyticsDebuggerPayload = {
  eventName: string;
  destinationEvents: AnalyticsDebuggerEvent[];
};

// EVI-1486 LOCAL VERIFICATION (uncommitted): default the debugger to ON in
// localhost so MCP-driven manual testing can read every event payload via
// browser_console_messages. REVERT BEFORE COMMIT.
let analyticsDebuggerEnabled =
  typeof window !== 'undefined' && getEnvironment() === ENV_NAME.LOCALHOST;
let analyticsDebuggerInitialized = false;

const isAnalyticsDebuggerAvailable = () =>
  typeof window !== 'undefined' && getEnvironment() !== ENV_NAME.PROD;

declare global {
  interface Window {
    toggleAnalyticsDebugger?: () => void;
  }
}

export const initAnalyticsDebugger = () => {
  if (!isAnalyticsDebuggerAvailable() || analyticsDebuggerInitialized) {
    return;
  }

  window.toggleAnalyticsDebugger = () => {
    analyticsDebuggerEnabled = !analyticsDebuggerEnabled;
    console.info(
      `[Analytics Debugger] ${analyticsDebuggerEnabled ? 'enabled' : 'disabled'}`
    );
  };

  analyticsDebuggerInitialized = true;
};

export const logTrackedAnalyticsEvent = ({
  eventName,
  destinationEvents,
}: AnalyticsDebuggerPayload) => {
  // Inspector panel: keep an in-memory + sessionStorage-backed log of every
  // dispatched event in any non-production environment, independent of the
  // console debugger toggle. The panel is the primary surface; the console
  // log below is opt-in via `toggleAnalyticsDebugger()`.
  if (isAnalyticsDebuggerAvailable()) {
    const inspectorEvent = {
      eventName,
      amplitudeUserId: amplitude.getUserId(),
      destinations: destinationEvents.map((event) => ({
        destination: event.destination,
        props: event.props,
      })),
    };
    // Widget webview forwards to the main window's inspector; else push locally.
    if (!forwardWidgetAnalyticsEvent(inspectorEvent)) {
      pushAnalyticsInspectorEvent(inspectorEvent);
    }
  }

  if (!isAnalyticsDebuggerAvailable() || !analyticsDebuggerEnabled) {
    return;
  }

  console.groupCollapsed(
    '%c[Analytics Debugger]%c %s',
    'background:#0f172a;color:#f8fafc;padding:2px 8px;border-radius:999px;font-weight:700;',
    'color:#0ea5e9;font-weight:700;',
    eventName
  );
  console.log(
    '%cDestinations',
    'color:#7c3aed;font-weight:700;',
    destinationEvents.map((event) => ({
      destination: event.destination,
      eventName: event.eventName,
      props: event.props,
    }))
  );
  // EVI-1486 LOCAL VERIFICATION (uncommitted): stringified mirror so the MCP
  // browser_console_messages tool (which flattens object args to
  // "[object Object]") can read the payload. REVERT BEFORE COMMIT.
  try {
    const stringified = JSON.stringify(
      { eventName, destinationEvents },
      (_k: string, v: unknown) =>
        typeof v === 'string' && v.length > 500 ? `${v.slice(0, 500)}…[truncated]` : v
    );
    console.log(`[AnalyticsDebuggerJSON] ${stringified}`);
  } catch {
    // ignore serialization errors (cyclic refs etc.)
  }
  console.groupEnd();
};
